/**
 * ============================================================================
 * SnapshotSigningTray — Phase 6b frontend (Snapshot Signing Tray)
 * Owner: Claude 3 (Frontend & UX).
 * Route: /quality-metrics/snapshots
 * Purpose: list of monthly quality-metric snapshots awaiting QA Director or
 *          Site Quality Head signature. Each snapshot is shown as a card with
 *          its scope, period, content_hash, status, and (if signed) signer.
 *          Pending snapshots expose a "Review & Sign" action.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Editorial layout, generous whitespace.
 *   - SIGNED cards de-emphasize the action area; the badge becomes the
 *     primary affordance (status, not "sign again").
 *   - VOIDED cards explain why and never offer a sign action.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './SnapshotSigningTray.module.css';
import {
  useSnapshotsQuery,
  type QualitySnapshot,
  type SnapshotStatus,
} from './useSnapshotsQuery';
import { SignSnapshotDialog } from './SignSnapshotDialog';

const STATUS_CLASS: Record<SnapshotStatus, string> = {
  PENDING_SIGN: styles.statusPending,
  SIGNED: styles.statusSigned,
  VOIDED: styles.statusVoided,
};

export function SnapshotSigningTray() {
  const { t, i18n } = useTranslation();
  const snapshotsQ = useSnapshotsQuery();
  const [signTarget, setSignTarget] = useState<QualitySnapshot | null>(null);

  const items: QualitySnapshot[] = snapshotsQ.data?.items ?? [];

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'long',
      }),
    [i18n.language],
  );

  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  // Pending count drives the page header summary.
  const pendingCount = useMemo(
    () => items.filter((s) => s.status === 'PENDING_SIGN').length,
    [items],
  );

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('qualityMetrics.snapshots.eyebrow', {
          defaultValue: 'Quality',
        })}
        title={t('qualityMetrics.snapshots.title', {
          defaultValue: 'Quality-Metric Snapshots',
        })}
        subtitle={t('qualityMetrics.snapshots.subtitle', {
          defaultValue:
            'Monthly snapshots awaiting QA Director / Site Quality Head signature. Once signed, snapshots are immutable.',
        })}
        actions={
          pendingCount > 0 ? (
            <span className={styles.pendingChip} aria-live="polite">
              {t('qualityMetrics.snapshots.pendingChip', {
                defaultValue: '{{count}} pending',
                count: pendingCount,
              })}
            </span>
          ) : undefined
        }
      />

      <Card>
        <CardHeader>
          <h2 className={wstyles.sectionTitle}>
            {t('qualityMetrics.snapshots.list.title', {
              defaultValue: 'Snapshots',
            })}
          </h2>
        </CardHeader>
        <CardBody>
          {snapshotsQ.isLoading && (
            <div
              role="status"
              aria-live="polite"
              aria-label={t('common.loading', { defaultValue: 'Loading…' })}
            >
              {[0, 1, 2].map((i) => (
                <div key={i} className={styles.skeletonCard} aria-hidden="true" />
              ))}
            </div>
          )}

          {snapshotsQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('qualityMetrics.snapshots.errors.loadFailed', {
                  defaultValue: 'Could not load quality-metric snapshots.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => snapshotsQ.refetch()}
              >
                {t('qualityMetrics.actions.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {!snapshotsQ.isLoading && !snapshotsQ.isError && items.length === 0 && (
            <p className={styles.emptyState}>
              {t('qualityMetrics.snapshots.empty', {
                defaultValue:
                  'No snapshots yet — metrics-svc will publish the first one at the next month boundary.',
              })}
            </p>
          )}

          {items.length > 0 && (
            <ul
              className={styles.cardList}
              aria-label={t('qualityMetrics.snapshots.listAria', {
                defaultValue: 'Quality-metric snapshots',
              })}
            >
              {items.map((snap) => {
                const statusLabel = t(
                  `qualityMetrics.snapshots.status.${snap.status}.label`,
                  { defaultValue: snap.status },
                );
                const scopeLabel = t(
                  `qualityMetrics.snapshots.scope.${snap.scope}`,
                  {
                    defaultValue: snap.scope === 'TENANT' ? 'Tenant' : 'Site',
                  },
                );
                const monthLabel = dateFmt.format(new Date(snap.period_start));

                return (
                  <li key={snap.snapshot_id} className={styles.cardListItem}>
                    <article
                      className={`${styles.snapshotCard} ${snap.status === 'VOIDED' ? styles.snapshotCardVoided : ''}`}
                      aria-labelledby={`snap-title-${snap.snapshot_id}`}
                    >
                      <header className={styles.snapshotHeader}>
                        <div>
                          <h3
                            id={`snap-title-${snap.snapshot_id}`}
                            className={styles.snapshotTitle}
                          >
                            {monthLabel}
                            <span className={styles.snapshotScopeSep} aria-hidden="true"> · </span>
                            <span className={styles.snapshotScope}>
                              {scopeLabel}
                              {snap.site_code ? ` (${snap.site_code})` : ''}
                            </span>
                          </h3>
                          <p className={styles.snapshotMeta}>
                            {t('qualityMetrics.snapshots.periodLabel', {
                              defaultValue: '{{start}} – {{end}}',
                              start: dayFmt.format(new Date(snap.period_start)),
                              end: dayFmt.format(new Date(snap.period_end)),
                            })}
                          </p>
                        </div>
                        <span
                          className={`${styles.statusBadge} ${STATUS_CLASS[snap.status]}`}
                          aria-label={t(
                            `qualityMetrics.snapshots.status.${snap.status}.aria`,
                            { defaultValue: `Status: ${statusLabel}` },
                          )}
                        >
                          {statusLabel}
                        </span>
                      </header>

                      <dl className={styles.snapshotFacts}>
                        <div className={styles.factRow}>
                          <dt className={styles.factKey}>
                            {t('qualityMetrics.snapshots.facts.contentHash', {
                              defaultValue: 'Content hash',
                            })}
                          </dt>
                          <dd className={styles.factValueMono}>
                            <code>{snap.content_hash}</code>
                          </dd>
                        </div>
                        <div className={styles.factRow}>
                          <dt className={styles.factKey}>
                            {t('qualityMetrics.snapshots.facts.generated', {
                              defaultValue: 'Generated',
                            })}
                          </dt>
                          <dd className={styles.factValue}>
                            <time dateTime={snap.generated_at_utc}>
                              {dayFmt.format(new Date(snap.generated_at_utc))}
                            </time>
                          </dd>
                        </div>
                        {snap.signed_by && (
                          <div className={styles.factRow}>
                            <dt className={styles.factKey}>
                              {t('qualityMetrics.snapshots.facts.signedBy', {
                                defaultValue: 'Signed by',
                              })}
                            </dt>
                            <dd className={styles.factValue}>
                              {snap.signed_by}
                              {snap.signed_at_utc && (
                                <>
                                  {' · '}
                                  <time dateTime={snap.signed_at_utc}>
                                    {dayFmt.format(new Date(snap.signed_at_utc))}
                                  </time>
                                </>
                              )}
                            </dd>
                          </div>
                        )}
                      </dl>

                      {snap.status === 'PENDING_SIGN' && (
                        <footer className={styles.snapshotActions}>
                          <Button
                            variant="primary"
                            onClick={() => setSignTarget(snap)}
                            aria-label={t(
                              'qualityMetrics.snapshots.actions.reviewSignAria',
                              {
                                defaultValue:
                                  'Review and sign snapshot for {{month}} ({{scope}})',
                                month: monthLabel,
                                scope: scopeLabel,
                              },
                            )}
                          >
                            {t('qualityMetrics.snapshots.actions.reviewSign', {
                              defaultValue: 'Review & Sign',
                            })}
                          </Button>
                        </footer>
                      )}

                      {snap.status === 'VOIDED' && (
                        <div className={styles.voidedNote} role="note">
                          {t('qualityMetrics.snapshots.voidedNote', {
                            defaultValue:
                              'This snapshot was voided because the underlying period was re-computed. A replacement snapshot has been issued.',
                          })}
                        </div>
                      )}
                    </article>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {signTarget && (
        <SignSnapshotDialog
          snapshot={signTarget}
          onClose={() => setSignTarget(null)}
          onSigned={() => {
            setSignTarget(null);
            snapshotsQ.refetch();
          }}
        />
      )}
    </div>
  );
}
